import { Paperclip } from "lucide-react";

import { Button } from "@needl-ai/common/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";

import { useFileUploadContext } from "@/contexts/FileUploadContext";
import { useAskNeedlUpload } from "@/hooks/useAskNeedlUpload";
import { shouldDisableFileUpload } from "@/utils/attachmentUtils";

export const AttachmentButton = ({
  disabled = false,
  className = "",
}: AttachmentButtonProps) => {
  const { fileInputRef, handleFileInputClick, handleFileSelect } =
    useAskNeedlUpload();
  const { state } = useFileUploadContext();

  const isFileUploadDisabled = shouldDisableFileUpload(
    state.attachments,
    state.attachment_feed_id
  );
  const isDisabled = disabled || isFileUploadDisabled;

  const getTooltipText = () => {
    if (isFileUploadDisabled) {
      return "Please wait for current upload to complete before adding more files";
    }
    if (disabled) {
      return "File upload not available";
    }
    return "Attach files";
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handleFileInputClick}
            disabled={isDisabled}
            className={`h-8 w-8 p-0 ${className} ${
              isFileUploadDisabled ? "opacity-50 cursor-not-allowed" : ""
            } text-gray-700 hover:text-gray-900 hover:bg-gray-50 dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-700`}
            data-testid="upload-button"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{getTooltipText()}</p>
        </TooltipContent>
      </Tooltip>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        disabled={isDisabled}
        accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.xls,.ppt,.pptx"
      />
    </>
  );
};

type AttachmentButtonProps = {
  disabled?: boolean;
  className?: string;
};
